import { lazy, Suspense, useState, type ReactNode } from "react";
import { Box, Collapse, Paper, Skeleton, Stack } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import { routePaths } from "../../routes/navigation";
// import Client360DetailSectionsCard from "./client360/panel/cards/Client360DetailSectionsCard";
import Client360HeroCard from "./client360/panel/cards/Client360HeroCard";
// import Client360IdentityCard from "./client360/panel/cards/Client360IdentityCard";
// import Client360OpportunitiesCard from "./client360/panel/cards/Client360OpportunitiesCard";
const Client360RedCardCard = lazy(() => import("./client360/red-card/Client360RedCardCard"));
const Client360RelatedCompaniesCard = lazy(() => import("./client360/related-companies/Client360RelatedCompaniesCard"));
// import Client360RecommendedActionCard from "./client360/panel/cards/Client360RecommendedActionCard";
import Client360StatsGrid from "./client360/panel/cards/Client360StatsGrid";
import type { Client360PanelProps } from "./client360/panel/types";
import { useClient360PanelData } from "./client360/panel/useClient360PanelData";

function CollapsibleSection({
    alwaysExpanded = false,
    children,
    defaultExpanded = true,
    title,
}: {
    alwaysExpanded?: boolean;
    children: ReactNode;
    defaultExpanded?: boolean;
    title: string;
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const isOpen = alwaysExpanded || expanded;

    return (
        <Box
            sx={{
                border: "1px solid rgba(216, 225, 235, 0.9)",
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: "rgba(255, 255, 255, 0.72)",
            }}
        >
            <Box
                onClick={alwaysExpanded ? undefined : () => setExpanded((value) => !value)}
                sx={{
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: alwaysExpanded ? "default" : "pointer",
                    bgcolor: "rgba(248, 251, 252, 0.96)",
                }}
            >
                <Box
                    component="span"
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "primary.main",
                        textAlign: "center",
                    }}
                >
                    {title}
                </Box>
                {!alwaysExpanded ? (
                    <Box sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                        {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                ) : null}
            </Box>
            {alwaysExpanded ? children : <Collapse in={isOpen}>{children}</Collapse>}
        </Box>
    );
}

export default function Client360Panel({
    canEditLoyaltyCard = false,
    canManageRelatedProfiles,
    // detailSections,
    isHistoryLoading = false,
    issues,
    loyaltyCard,
    loyaltyAuditEnabled = false,
    loyaltyAuditEvents = [],
    loyaltyUsageHistory = [],
    loyaltyCardSaveError = null,
    isLoyaltyAuditLoading = false,
    isLoyaltyCardSaving = false,
    onSaveLoyaltyCard,
    manualRelatedProfiles,
    // nextAction,
    onAddRelatedProfile,
    onRemoveRelatedProfile,
    orders,
    profile,
    relatedProfiles,
    traderDefect,
}: Client360PanelProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { /* opportunities, */ snapshot, stats } = useClient360PanelData({
        issues,
        loyaltyCard,
        orders,
        profile,
        traderDefect,
    });

    const currentPath = `${location.pathname}${location.search}`;
    // const loyaltyPath = loyaltyCard
    //     ? `${routePaths.loyalty}?card=${encodeURIComponent(loyaltyCard.cardNumber)}&returnTo=${encodeURIComponent(currentPath)}`
    //     : `${routePaths.loyalty}?search=${encodeURIComponent(profile.customer)}&returnTo=${encodeURIComponent(currentPath)}`;
    // const ordersPath = `${routePaths.orders}?profile=${encodeURIComponent(profile.profileId)}&customer=${encodeURIComponent(profile.customer)}
    // &returnTo=${encodeURIComponent(currentPath)}`;
    const openRelatedProfile = (targetProfile: Client360PanelProps["profile"]) => {
        navigate(`${routePaths.client360}?profile=${encodeURIComponent(targetProfile.profileId)}&customer=${encodeURIComponent(targetProfile.customer)}&returnTo=${encodeURIComponent(currentPath)}`);
    };

    return (
        <Paper className="table-card client-360-panel" elevation={0} sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Stack spacing={1.5}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            lg: "220px minmax(0, 1fr)",
                        },
                        gap: 1.5,
                        alignItems: "start",
                    }}
                >
                    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                        {/*<Client360IdentityCard profile={profile} />*/}
                        <Suspense fallback={<Skeleton variant="rounded" height={160} />}>
                            <Client360RelatedCompaniesCard
                                canManage={canManageRelatedProfiles}
                                currentProfile={profile}
                                currentProfileId={profile.profileId}
                                manualRelatedProfiles={manualRelatedProfiles}
                                onAddProfileLink={onAddRelatedProfile}
                                onOpenProfile={openRelatedProfile}
                                onRemoveProfileLink={onRemoveRelatedProfile}
                                relatedProfiles={relatedProfiles}
                            />
                        </Suspense>
                    </Stack>

                    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                        <Client360HeroCard
                            latestOrder={orders[0] ?? null}
                            orders={orders}
                            profile={profile}
                            snapshot={snapshot}
                        />

                        <Client360StatsGrid stats={stats} />
                        {isHistoryLoading ? (
                            <Stack spacing={1} aria-busy="true" aria-label="Зареждане на история">
                                <Skeleton variant="rounded" height={48} />
                            </Stack>
                        ) : null}

                        <CollapsibleSection title="" alwaysExpanded>
                            <Suspense fallback={<Skeleton variant="rounded" height={220} />}>
                                <Client360RedCardCard
                                    auditEnabled={loyaltyAuditEnabled}
                                    auditEvents={loyaltyAuditEvents}
                                    canEdit={canEditLoyaltyCard}
                                    isAuditLoading={isLoyaltyAuditLoading}
                                    isSaving={isLoyaltyCardSaving}
                                    loyaltyCard={loyaltyCard}
                                    usageHistory={loyaltyUsageHistory}
                                    onSave={onSaveLoyaltyCard ?? (async () => undefined)}
                                    profile={profile}
                                    saveError={loyaltyCardSaveError}
                                />
                            </Suspense>
                        </CollapsibleSection>



                        {/* за сега не ми трябва за това е закоментиран *?}
                        {/*{detailSections.length > 0 ? (*/}
                        {/*    <CollapsibleSection title="Детайлен панел" defaultExpanded={false}>*/}
                        {/*        <Client360DetailSectionsCard orders={orders} profile={profile} sections={detailSections} />*/}
                        {/*    </CollapsibleSection>*/}
                        {/*) : null}*/}
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    );
}
